import React, { useState, useEffect } from 'react';
import { Button } from '../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { Badge } from '../../../../../../../components/ui/badge';
import { Input } from '../../../../../../../components/ui/input';
import { Label } from '../../../../../../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../../../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../../../../../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../../../../components/ui/select';
import {
    Settings,
    Wrench,
    Code,
    ChevronDown,
    CheckCircle,
    AlertCircle,
    Info,
    Download,
    RefreshCw,
    ArrowLeft,
    Edit3,
} from 'lucide-react';

// Import the weld parameters data
import weldParametersData from './weldParamters.json';

// Import reusable components
import { JobParameters } from '.';
import SoftwareVersion from './SoftwareVersion';
import WeldParametersTable from './WeldParametersTable';
import RenamePass from './RenamePass';

interface P625ParametersProps {
    jobData?: any[];
    projectId?: number;
    onRenameSuccess?: () => void;
    canEdit?: boolean;
}

interface JobParameter {
    job_number: string;
    [key: string]: any;
}

interface WeldParameter {
    [key: string]: any;
}

interface SoftwareVersion {
    [key: string]: any;
}

const P625Parameters: React.FC<P625ParametersProps> = ({
    jobData,
    projectId,
    onRenameSuccess,
    canEdit = true,
}) => {
    const [activeTab, setActiveTab] = useState('job-parameters');
    const [selectedJob, setSelectedJob] = useState<string>('');
    const [selectedPipeParam, setSelectedPipeParam] = useState<string>('');

    // Extract job numbers from the array
    const jobNumbers = React.useMemo(() => {
        if (!jobData || !Array.isArray(jobData)) return [];
        return jobData.map((item: JobParameter) => item.job_number).filter(Boolean);
    }, [jobData]);

    // Set first job as default
    useEffect(() => {
        if (jobNumbers.length > 0 && !selectedJob) {
            setSelectedJob(jobNumbers[0]);
        }
    }, [jobNumbers, selectedJob]);

    // Get selected job data
    const selectedJobData = React.useMemo(() => {
        if (!selectedJob || !jobData) return null;
        return jobData.find((item: JobParameter) => item.job_number === selectedJob);
    }, [selectedJob, jobData]);

    const handleJobSelect = (jobNum: string) => {
        setSelectedJob(jobNum);
        // Reset pipe parameter when job changes
        setSelectedPipeParam('');
    };

    // Extract available weld parameter keys for selected job
    const weldParameterKeys = React.useMemo(() => {
        if (!selectedJobData?.parsed_json_data) return [];
        
        const keys = Object.keys(selectedJobData.parsed_json_data)
            .filter(key => key.includes('WELD PARAMETERS'))
            .map(key => {
                const parts = key.split(';');
                return {
                    fullKey: key,
                    displayName: parts[1] || parts[0]
                };
            });
        
        return keys;
    }, [selectedJobData]);

    // Set first weld parameter as default
    useEffect(() => {
        if (weldParameterKeys.length > 0 && !selectedPipeParam) {
            setSelectedPipeParam(weldParameterKeys[0].fullKey);
        }
    }, [weldParameterKeys, selectedPipeParam]);

    // Check if the job needs renaming (is_renamed = false)
    const needsRenaming = React.useMemo(() => {
        return true;
        if (!selectedJobData) return false;
        return selectedJobData.is_renamed === false;
    }, [selectedJobData]);

    return (
        <div className="h-full flex flex-col">
            {/* Job Selection — only render if multiple jobs. Single-job info is shown in modal header. */}
            {jobNumbers.length > 1 && (
                <div className="px-6 pt-4 pb-2 bg-white border-b border-[#E2E5EA]">
                    <div className="flex items-center gap-3">
                        <Label className="text-[12px] font-medium text-text-grey whitespace-nowrap">
                            Select Machine/Job
                        </Label>
                        <Select value={selectedJob} onValueChange={handleJobSelect}>
                            <SelectTrigger className="w-[260px] h-9">
                                <SelectValue placeholder="Choose a job number..." />
                            </SelectTrigger>
                            <SelectContent>
                                {jobNumbers.map((jobNumber) => (
                                    <SelectItem key={jobNumber} value={jobNumber}>
                                        P625: {jobNumber}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}

            <div className="flex-1 min-h-0 overflow-hidden">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col min-h-0 overflow-hidden">
                    <TabsList className="h-auto w-full justify-start gap-6 rounded-none border-b border-[#E2E5EA] bg-white px-6 py-0 flex-shrink-0">
                        <TabsTrigger
                            value="job-parameters"
                            className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                            <div className="flex items-center gap-2">
                                <Settings className="h-4 w-4" />
                                Job Parameters
                            </div>
                        </TabsTrigger>
                        <TabsTrigger
                            value="weld-parameters"
                            className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                            <div className="flex items-center gap-2">
                                <Wrench className="h-4 w-4" />
                                Weld Parameters
                            </div>
                        </TabsTrigger>
                        <TabsTrigger
                            value="software-versions"
                            className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                            <div className="flex items-center gap-2">
                                <Code className="h-4 w-4" />
                                Software Versions
                            </div>
                        </TabsTrigger>
                        {needsRenaming && canEdit && (
                            <TabsTrigger
                                value="rename-pass"
                                className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                            >
                                <div className="flex items-center gap-2">
                                    <Edit3 className="h-4 w-4" />
                                    Rename Pass
                                </div>
                            </TabsTrigger>
                        )}
                    </TabsList>

                    <div className="flex-1 min-h-0 overflow-hidden bg-page-bg">
                        {/* Job Parameters Tab */}
                        <TabsContent value="job-parameters" className="h-full overflow-auto mt-0">
                            {selectedJobData ? (
                                <JobParameters jobData={selectedJobData} />
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <Settings className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Job Parameters</h3>
                                    <p>Please select a job to view parameters</p>
                                </div>
                            )}
                        </TabsContent>

                        {/* Weld Parameters Tab */}
                        <TabsContent value="weld-parameters" className="h-full overflow-hidden mt-0">
                            {selectedJobData ? (
                                <WeldParametersTable jobData={selectedJobData} />
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <Wrench className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Weld Parameters</h3>
                                    <p>Please select a job to view weld parameters</p>
                                </div>
                            )}
                        </TabsContent>

                        {/* Software Versions Tab */}
                        <TabsContent value="software-versions" className="h-full overflow-auto mt-0">
                            {selectedJobData ? (
                                <SoftwareVersion jobData={selectedJobData} />
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <Code className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Software Versions</h3>
                                    <p>Please select a job to view software version information</p>
                                </div>
                            )}
                        </TabsContent>

                        {needsRenaming && canEdit && (
                            <TabsContent value="rename-pass" className="mt-0 h-full flex flex-col overflow-hidden">
                                {selectedJobData && selectedPipeParam ? (
                                    <RenamePass 
                                        jobData={selectedJobData} 
                                        projectId={projectId}
                                        pipeParamKey={selectedPipeParam}
                                        onRenameSuccess={onRenameSuccess}
                                    />
                                ) : (
                                    <div className="text-center py-12 text-gray-500">
                                        <Edit3 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">Rename Pass</h3>
                                        <p>
                                            {!selectedJobData 
                                                ? 'Please select a job to rename passes' 
                                                : !selectedPipeParam 
                                                    ? 'Please select a weld parameter to rename passes'
                                                    : 'No data available'}
                                        </p>
                                    </div>
                                )}
                            </TabsContent>
                        )}
                    </div>
                </Tabs>
            </div>
        </div>
    );
};

export default P625Parameters;
